// src/pages/Faq.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HighLogo from "../../components/HighLogo";
import logo from "../../../assets/logo.png";

type FAQItem = {
  id: string;
  q: string;
  a: string;
};

type FAQGroup = {
  id: string;
  title: string;
  hint: string;
  items: FAQItem[];
};

const Faq: React.FC = () => {
  const navigate = useNavigate();
  const loc = useLocation();
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [activeGroup, setActiveGroup] = useState<string>("pg-top");

  // Lock page scroll (only inner panel scrolls)
  useEffect(() => {
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  const groups: FAQGroup[] = useMemo(
    () => [
      {
        id: "posting-gallery",
        title: "Posting & Gallery",
        hint: "How the feed works, how to post, formats, music, and viewing.",
        items: [
          {
            id: "what-is-gallery",
            q: "What is the Gallery?",
            a: "The Gallery is UNYDO’s main feed where you browse posts (photos/videos) from a selected location. Posts are organized by location scopes (city → country → nearby countries → broader).",
          },
          {
            id: "how-many-media",
            q: "How many photos/videos can I include in one post?",
            a: "A single post can include up to 8 media items (photos and/or videos).",
          },
          {
            id: "post-order",
            q: "How are posts ordered in the Gallery?",
            a: "Within each location scope, posts are typically shown newest-first (sorted by publishing time).",
          },
          {
            id: "change-location-feed",
            q: "Can I change the location of my Gallery feed?",
            a: "Yes. You can use Change Location to browse another city/country. This affects only what you see in the feed and does not change where your posts are tagged.",
          },
          {
            id: "presentation-mode",
            q: "What is Presentation mode?",
            a: "Presentation mode is the focused viewer for a post. It shows one media item per page so you can navigate left/right with arrows and close with the close button or Escape.",
          },
          {
            id: "music-on-posts",
            q: "Can I add music to my videos?",
            a: "If music is supported in your posting flow, you can attach a track to a video. Viewers see music info in the post viewer (desktop: on hover; mobile: via the Music button).",
          },
          {
            id: "filters",
            q: "Can I apply filters to photos/videos?",
            a: "Yes, if filters are available in your uploader. Apply the filter during preview before publishing.",
          },
          {
            id: "edit-after-post",
            q: "Can I edit a post after publishing?",
            a: "Typically, media edits are not available after publishing. If your app supports caption/metadata edits, you can change those in the post options.",
          },
          {
            id: "upload-failed",
            q: "My upload fails or gets stuck. What can I do?",
            a: "Try switching networks (Wi-Fi ↔ mobile), reduce file size, ensure the app has permissions, and retry. Large or long videos may fail more often.",
          },
          {
            id: "video-format",
            q: "What video format works best?",
            a: "For reliability, use common optimized formats (often MP4/H.264). Extremely high-bitrate or unusual codecs can cause upload/playback issues.",
          },
          {
            id: "why-post-not-visible",
            q: "Why can’t I see my post in the Gallery?",
            a: "Check your post visibility settings, refresh the feed, and confirm the post finished uploading. Also ensure you’re browsing the correct location scope.",
          },
          {
            id: "see-own-posts",
            q: "Where do I see my own posts?",
            a: "Your posts appear on your profile (MyProfile) and can appear in the Gallery based on their tagged location and your visibility settings.",
          },
          {
            id: "remove-media-before-post",
            q: "Can I remove a media item before I publish?",
            a: "Yes. In the preview step you can typically remove individual media items or reset the selection before publishing.",
          },
        ],
      },
      {
        id: "location-privacy",
        title: "Location & Privacy",
        hint: "Location usage, feed browsing vs posting, and privacy expectations.",
        items: [
          {
            id: "location-needed",
            q: "Why does UNYDO request location?",
            a: "Location is used to tag posts with a city/country and to show location-based posts in the Gallery. Location helps deliver “what’s happening around you.”",
          },
          {
            id: "change-location-vs-posting",
            q: "Does changing my feed location also change my posting location?",
            a: "No. Change Location affects browsing only. Posting uses the device’s current fetched location at the time you upload.",
          },
          {
            id: "what-location-stored",
            q: "What location data is stored with my post?",
            a: "Posts are typically stored with a derived city/country tag. Your exact GPS coordinates (if used at all) should be processed only as needed to determine city/country and not retained longer than necessary.",
          },
          {
            id: "turn-off-location",
            q: "Can I use UNYDO with location turned off?",
            a: "Some features may degrade. You may still browse, but the location-based feed may be less accurate or limited. Posting may require location to tag the post correctly.",
          },
          {
            id: "vpn-location",
            q: "Does a VPN change my post’s location?",
            a: "A VPN may affect network routing but should not change the device location fetched during posting. The post’s tag should reflect where it was actually created.",
          },
          {
            id: "who-sees-location",
            q: "Who can see my post location?",
            a: "Users typically see a location label (city/country) on posts to understand where the moment happened. Exact coordinates are not shown.",
          },
          {
            id: "hide-like-activity",
            q: "Can I hide my likes or activity?",
            a: "If your privacy settings support it, you may be able to limit who can see your activity. If not, likes are usually shown only as counts or in your private ‘My Likes’ list.",
          },
          {
            id: "profile-privacy",
            q: "Can I make my profile private?",
            a: "If private profiles are supported, you can restrict who can view your posts/profile. If not, your profile and posts are visible according to the app’s default public behavior.",
          },
          {
            id: "blocked-privacy",
            q: "Does blocking someone hide my content from them?",
            a: "In most designs, yes. Blocking should prevent profile visibility, chat, search discovery, and feed presence between both users.",
          },
          {
            id: "data-minimization",
            q: "Does UNYDO sell my data or track me for ads?",
            a: "UNYDO is designed to minimize data collection. If you run ads, the app should rely on clear settings and legal documents. Check the Data Protection Guideline for your specific implementation.",
          },
          {
            id: "reporting-privacy",
            q: "Are reports anonymous?",
            a: "Reporting is handled internally. Depending on policy, your identity may or may not be shared with the reported user. Most platforms do not expose reporter identity.",
          },
          {
            id: "delete-location-data",
            q: "If I delete a post, does the location tag disappear too?",
            a: "Yes—deleting a post removes the post and its associated location tag from user-facing areas. Some limited logs may remain temporarily for security/abuse prevention, as stated in legal docs.",
          },
        ],
      },
      {
        id: "chat-encryption",
        title: "Chat & Encryption",
        hint: "E2EE expectations, metadata, media, and chat behavior.",
        items: [
          {
            id: "is-chat-e2ee",
            q: "Is chat end-to-end encrypted (E2EE)?",
            a: "If E2EE is enabled, message content is encrypted on the sender device and decrypted only on the recipient device. Servers store unreadable ciphertext for delivery.",
          },
          {
            id: "chat-metadata",
            q: "What metadata exists even with E2EE?",
            a: "Typical metadata includes sender/recipient IDs, timestamps, delivery status, and file sizes. The content remains encrypted.",
          },
          {
            id: "can-unydo-read-chat",
            q: "Can UNYDO read my chat messages?",
            a: "With E2EE enabled, UNYDO cannot read message bodies. The system can still process limited metadata needed for routing and abuse prevention.",
          },
          {
            id: "send-media-chat",
            q: "Can I send photos/videos in chat?",
            a: "Yes, if enabled. With E2EE, media should be encrypted client-side before upload and stored as encrypted blobs.",
          },
          {
            id: "delete-chat-message",
            q: "Can I delete a chat message?",
            a: "That depends on your platform design. Some apps support local-only deletion, while others attempt best-effort deletion for both sides.",
          },
          {
            id: "lost-device-chat",
            q: "What happens if I lose my device?",
            a: "If chat keys are stored on the device, losing it can mean older encrypted messages cannot be decrypted on a new device. Use recovery options if available.",
          },
          {
            id: "message-not-delivered",
            q: "Why are my messages not delivering?",
            a: "Check your connection, verify the recipient is reachable, and ensure you’re not blocked. Also confirm the app has background/network permissions.",
          },
          {
            id: "blocked-chat",
            q: "Can I chat with a user I blocked?",
            a: "Usually no. Blocking should prevent messaging between both accounts until the block is removed.",
          },
          {
            id: "group-chats",
            q: "Does UNYDO support group chats?",
            a: "If group chat is enabled, it follows the same privacy rules. E2EE for groups requires additional key management; behavior depends on implementation.",
          },
          {
            id: "screenshots",
            q: "Does UNYDO prevent screenshots in chat?",
            a: "Some platforms can limit screenshots on mobile OS, but it’s not guaranteed. Assume recipients can capture what they see.",
          },
          {
            id: "chat-search",
            q: "Can I search within chats?",
            a: "If supported, you can search chat lists or message history. With E2EE, search may be local (on-device) rather than server-side.",
          },
          {
            id: "chat-attachments-fail",
            q: "Why does sending media fail in chat?",
            a: "Large files, poor connectivity, or unsupported formats can cause failures. Try smaller files and a stable network.",
          },
        ],
      },
      {
        id: "account-security",
        title: "Account & Security",
        hint: "Login, password, sessions, recovery, deletion, and account safety.",
        items: [
          {
            id: "forgot-password",
            q: "I forgot my password. What should I do?",
            a: "Use the “Forgot password” flow on the login screen. If you don’t receive an email, check spam and confirm you used the correct email address.",
          },
          {
            id: "change-email",
            q: "Can I change my account email?",
            a: "If supported, go to settings and update your email. You may need to re-authenticate or confirm the new address via email.",
          },
          {
            id: "change-password",
            q: "How do I change my password?",
            a: "Go to account settings and choose Change Password. For security, you may be asked to confirm your current password.",
          },
          {
            id: "reauth-why",
            q: "Why does UNYDO ask me to re-authenticate?",
            a: "Sensitive actions (email change, password change, account deletion) often require re-auth to prevent unauthorized changes.",
          },
          {
            id: "logout-everywhere",
            q: "Can I log out of all devices?",
            a: "If supported, there is a “Log out all sessions” option in security settings. Otherwise, changing your password may invalidate older sessions.",
          },
          {
            id: "suspicious-login",
            q: "I see suspicious activity. What should I do?",
            a: "Change your password immediately, log out of other sessions if available, and contact support with details.",
          },
          {
            id: "two-factor",
            q: "Does UNYDO support 2FA?",
            a: "If enabled on your platform, 2FA adds a second step to login. If you lose access to your second factor, use recovery options or contact support.",
          },
          {
            id: "account-deletion",
            q: "How do I delete my account?",
            a: "Go to settings and choose Delete Account. Some data may be removed, and some may be anonymized depending on legal and safety requirements.",
          },
          {
            id: "anonymized-what",
            q: "What does “anonymized” mean when I delete?",
            a: "It means identifying information is removed or replaced so content is no longer linked to you personally. Some records may remain for compliance/security but without your identity.",
          },
          {
            id: "blocked-users-list",
            q: "Where do I see users I blocked?",
            a: "In your privacy or safety settings you should find a Blocked Users list where you can unblock accounts.",
          },
          {
            id: "username-change",
            q: "Can I change my profile name?",
            a: "If supported, change it in MyProfile settings. Some platforms restrict frequent changes.",
          },
          {
            id: "device-change",
            q: "What happens if I switch phones?",
            a: "You can log in on the new device. For E2EE chat, older messages may not be readable if keys are device-bound unless you support key transfer or backups.",
          },
        ],
      },
      {
        id: "following-likes",
        title: "Likes, Following & Notifications",
        hint: "Engagement mechanics and alerts.",
        items: [
          {
            id: "like-unlike",
            q: "How do I like or unlike a post?",
            a: "Tap Like to like a post. Tap again to remove your like (unlike).",
          },
          {
            id: "see-liked-posts",
            q: "Where can I see posts I liked?",
            a: "In My Likes (or a similar section) you can review posts you’ve liked, if the feature is enabled.",
          },
          {
            id: "follow-unfollow",
            q: "How do I follow or unfollow someone?",
            a: "Tap Follow on their post or profile. Tap Unfollow to remove them from your Following list.",
          },
          {
            id: "followers-following",
            q: "What is the difference between Followers and Following?",
            a: "Following = the users you follow. Followers = users who follow you.",
          },
          {
            id: "follow-notification",
            q: "Do users get notified when I follow them?",
            a: "If notifications are enabled, yes—users may receive a follow notification showing your profile name and time.",
          },
          {
            id: "like-notification",
            q: "Do users get notified when I like their post?",
            a: "If enabled, yes—likes can trigger a notification to the creator.",
          },
          {
            id: "notification-settings",
            q: "Can I turn off notifications?",
            a: "Yes. In settings you can typically toggle notification categories like likes, follows, messages, and system alerts.",
          },
          {
            id: "notifications-not-working",
            q: "Why am I not receiving notifications?",
            a: "Check OS notification permissions, battery/background restrictions, and in-app notification toggles.",
          },
          {
            id: "mute-user",
            q: "Can I mute someone without blocking them?",
            a: "If the feature exists, muting stops their content/notifications without fully blocking. If not available, blocking is the strongest option.",
          },
          {
            id: "visibility-following",
            q: "Does following change what I see in the Gallery?",
            a: "Depending on design, following may affect ranking or provide a separate “Following/Preferences” view where followed users appear more often.",
          },
        ],
      },
      {
        id: "safety-reporting",
        title: "Safety, Reporting & Moderation",
        hint: "Blocking, reporting, rules, and what happens after.",
        items: [
          {
            id: "when-block",
            q: "When should I block a user?",
            a: "Block users to immediately stop unwanted interactions or harassment. Blocking typically prevents profile visibility and chat between both users.",
          },
          {
            id: "when-report",
            q: "When should I report a post or account?",
            a: "Report content that violates rules: harassment, illegal activity, hateful content, non-consensual imagery, scams, or IP infringement.",
          },
          {
            id: "what-happens-report",
            q: "What happens after I report something?",
            a: "Reports go into a moderation queue for review. Actions can include warnings, removals, suspensions, or bans depending on severity.",
          },
          {
            id: "appeal",
            q: "Can I appeal a moderation decision?",
            a: "If appeals are supported, you can contact support with details and request a review. Provide the post/user details and context.",
          },
          {
            id: "false-report",
            q: "What if someone falsely reports me?",
            a: "Moderation decisions should be based on policy and evidence. If you believe a decision is incorrect, use the appeal route.",
          },
          {
            id: "scams",
            q: "How do I avoid scams on UNYDO?",
            a: "Be careful with links, offers, or requests for money/personal info. Block and report suspicious accounts.",
          },
          {
            id: "danger-emergency",
            q: "What if someone is in immediate danger?",
            a: "UNYDO is not an emergency service. Contact local authorities or emergency services immediately.",
          },
          {
            id: "copyright",
            q: "What about copyright and reposting content?",
            a: "Only post content you own or have rights to use. If you receive a takedown notice, follow the platform’s process for disputes/counter-notices.",
          },
          {
            id: "hate-speech",
            q: "Does UNYDO allow hate speech or harassment?",
            a: "No. Content that targets individuals/groups with hate or harassment should be removed and may result in bans.",
          },
          {
            id: "privacy-violations",
            q: "What if someone shares my private information?",
            a: "Report the content immediately. Sharing private info without consent is typically prohibited and can lead to removal and enforcement actions.",
          },
        ],
      },
      {
        id: "ads-billing",
        title: "Ads & Billing (if enabled)",
        hint: "Campaign basics, review, and common ad issues.",
        items: [
          {
            id: "ads-required",
            q: "Do I need ads to use UNYDO?",
            a: "No. Ads (if available) are typically optional for promotion. Normal users can use the platform without running ads.",
          },
          {
            id: "create-ad",
            q: "How do I create an ad campaign?",
            a: "Go to the ads area, upload your creative, set targeting, choose budget, and submit for review.",
          },
          {
            id: "ad-targeting",
            q: "How does ad targeting work?",
            a: "Targeting can include regions, countries, cities, and possibly interest signals (e.g., hashtags) depending on your ad system configuration.",
          },
          {
            id: "ad-review-time",
            q: "How long does ad review take?",
            a: "Review times vary. Ads may be approved quickly or require manual review if flagged by policy checks.",
          },
          {
            id: "ad-rejected",
            q: "Why was my ad rejected?",
            a: "Common reasons include prohibited content, misleading claims, unsafe content, broken URLs, or policy violations. Adjust and resubmit.",
          },
          {
            id: "billing-when-charged",
            q: "When do I get charged for ads?",
            a: "Billing typically occurs based on delivery (impressions/clicks) or as configured by your billing model. Check your ads dashboard/invoices.",
          },
          {
            id: "ad-spend-not-delivering",
            q: "My ad is not spending or delivering. Why?",
            a: "Targeting may be too narrow, budget too low, creative rejected, or inventory limited. Broaden targeting and ensure it’s approved.",
          },
          {
            id: "ad-creative-best",
            q: "What makes a good ad creative?",
            a: "Short videos, clear message, readable text, and a strong call-to-action. Ensure your landing page loads quickly and matches the ad.",
          },
          {
            id: "refunds",
            q: "Do you offer refunds for ads?",
            a: "Refund policies depend on your billing terms and local laws. Check your Advertisement Guide and billing terms.",
          },
          {
            id: "invoices",
            q: "Where do I find invoices?",
            a: "If available, invoices are shown in your ads/billing section. If you can’t find them, contact support with campaign details.",
          },
        ],
      },
      {
        id: "troubleshooting",
        title: "Troubleshooting",
        hint: "Common issues and quick fixes.",
        items: [
          {
            id: "app-crashes",
            q: "The app crashes or freezes. What should I do?",
            a: "Restart the app, update to the latest version, and try again. If it continues, reinstall and contact support with device/version info.",
          },
          {
            id: "cant-login",
            q: "I can’t log in. What can cause this?",
            a: "Wrong credentials, network issues, account restrictions, or email verification issues. Try resetting your password and checking your connection.",
          },
          {
            id: "slow-feed",
            q: "The Gallery feels slow. How can I improve it?",
            a: "Use a stable connection, close background downloads, and try a different location scope. Very busy locations may load more data.",
          },
          {
            id: "no-sound",
            q: "My video has no sound. Why?",
            a: "Check your device volume/silent mode, media permissions, and whether the video includes an audio track. Also confirm the post has music/audio enabled.",
          },
          {
            id: "camera-permission",
            q: "Camera or photos permission is missing. How do I fix it?",
            a: "Enable permissions in OS settings for UNYDO (Camera/Photos/Media). Then restart the app.",
          },
          {
            id: "location-permission",
            q: "Location permission is missing. How do I fix it?",
            a: "Enable Location permission in OS settings for UNYDO. If location is required for posting, you may not be able to publish without it.",
          },
          {
            id: "notifications-disabled",
            q: "Notifications are enabled in the app but I still don’t get them.",
            a: "Check OS notification settings and battery optimization/background restrictions. Some devices block background delivery.",
          },
          {
            id: "blocked-content",
            q: "Why do I see fewer posts than my friend in the same city?",
            a: "Blocks, privacy settings, content removals, or different location selection can change what you see. Confirm your feed location and refresh.",
          },
          {
            id: "profile-not-updating",
            q: "My profile changes don’t show up.",
            a: "Refresh the page/app and ensure the save/upload completed. If you’re on a slow network, changes might appear after a short delay.",
          },
          {
            id: "contact-support-when",
            q: "When should I contact support?",
            a: "Contact support if you have account access issues, billing questions, safety concerns, or persistent bugs that basic troubleshooting doesn’t solve.",
          },
        ],
      },
    ],
    []
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;

    const match = (s: string) => s.toLowerCase().includes(q);

    return groups
      .map((g) => {
        const items = g.items.filter((it) => match(it.q) || match(it.a));
        if (!items.length) return null;
        return { ...g, items };
      })
      .filter(Boolean) as FAQGroup[];
  }, [groups, query]);

  const flatIds = useMemo(() => {
    const ids: string[] = ["pg-top"];
    for (const g of groups) {
      ids.push(g.id);
    }
    return ids;
  }, [groups]);

  const scrollTo = (id: string) => {
    const container = document.getElementById("pg-scroll");
    const el = document.getElementById(id);
    if (!container || !el) return;

    const top =
      el.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop -
      12;

    container.scrollTo({ top, behavior: "smooth" });
    navigate(`#${id}`, { replace: true });
  };

  // deep link on load: /help/faq#group-id or #question-id
  useEffect(() => {
    const hash = (loc.hash || "").replace("#", "").trim();
    if (!hash) return;

    const t = window.setTimeout(() => {
      // If it's a question id, open it and scroll
      const isQuestion = groups.some((g) => g.items.some((it) => it.id === hash));
      if (isQuestion) setOpenId(hash);
      scrollTo(hash);
    }, 0);

    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Active anchor tracking inside scroll container (groups only)
  useEffect(() => {
    const container = document.getElementById("pg-scroll");
    if (!container) return;

    const handler = () => {
      let current = "pg-top";
      for (const id of flatIds) {
        const el = document.getElementById(id);
        if (!el) continue;

        const elTop =
          el.getBoundingClientRect().top - container.getBoundingClientRect().top;
        if (elTop <= 120) current = id;
      }
      setActiveGroup(current);
    };

    handler();
    container.addEventListener("scroll", handler, { passive: true });
    return () => container.removeEventListener("scroll", handler as any);
  }, [flatIds]);

  const toggle = (id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
    navigate(`#${id}`, { replace: true });
  };

  return (
    <div style={shell}>
      {/* Top bar */}
      <header style={header}>
        <div style={headerLeft} onClick={() => navigate("/gallery")}>
  <HighLogo
    src={logo}
    style={{ width: 100, display: "block", marginLeft: "-20px", marginTop: "6px" }}
    padding={0}
    onClick={() => navigate("/gallery")}
  />
</div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", marginRight: 70, }}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={ghostBtn}
            aria-label="Back"
            title="Back"
          >
            ← Back     
          </button>     
        </div>
      </header>

      <main style={main}>
        {/* Jump bar */}
        <section style={jumpBar}>
          <div style={jumpLeft}>
            <div style={jumpTitle}>FAQ</div>
            <div style={jumpHint}>Browse by category, or search for answers.</div>
          </div>

          <div style={jumpRight}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions…"
              style={searchInput}
              aria-label="Search FAQ"
            />
          </div>

          <div style={jumpPillsWrap}>
            <button
              type="button"
              onClick={() => scrollTo("pg-top")}
              className="pg-link"
              style={{ ...pillBtn, ...(activeGroup === "pg-top" ? pillActive : null) }}
            >
              Overview
            </button>

            {groups.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => scrollTo(g.id)}
                className="pg-link"
                style={{ ...pillBtn, ...(activeGroup === g.id ? pillActive : null) }}
                title={g.title}
              >
                {g.title}
              </button>
            ))}
          </div>
        </section>

        {/* Content card */}
        <section style={contentCard}>
          <div id="pg-scroll" style={contentScroll}>
            <div id="pg-top" />

            <div style={pageIntro}>
              <h1 style={h1}>Frequently Asked Questions</h1>
              <p style={subhead}>
                Find quick answers about using UNYDO. Use the search bar to filter questions
                instantly, or browse by category below.
              </p>
            </div>

            {filtered.map((g) => (
              <section key={g.id} id={g.id} style={groupBlock}>
                <div style={groupHead}>
                  <div style={groupTitle}>{g.title}</div>
                  <div style={groupHint}>{g.hint}</div>
                </div>

                <div style={qaGrid}>
                  {g.items.map((it) => {
                    const open = openId === it.id;
                    return (
                      <article key={it.id} id={it.id} style={qaCard}>
                        <button
                          type="button"
                          onClick={() => toggle(it.id)}
                          className="pg-link"
                          style={qBtn(open)}
                          aria-expanded={open}
                          aria-controls={`${it.id}-a`}
                        >
                          <span style={qText}>{it.q}</span>
                          <span style={chev}>{open ? "−" : "+"}</span>
                        </button>

                        {open && (
                          <div id={`${it.id}-a`} style={aWrap}>
                            <div style={aText}>{it.a}</div>

                            <div style={qaFooter}>
                              <button
                                type="button"
                                className="pg-link"
                                onClick={() => {
                                  const url = `${window.location.origin}${window.location.pathname}#${it.id}`;
                                  navigator.clipboard?.writeText(url).catch(() => {});
                                }}
                                style={miniBtn}
                                title="Copy link"
                                aria-label="Copy link"
                              >
                                ⧉ Copy link
                              </button>

                              <button
                                type="button"
                                className="pg-link"
                                onClick={() => scrollTo("pg-top")}
                                style={miniBtn}
                                title="Back to top"
                              >
                                ↑ Top
                              </button>
                            </div>
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </section>
      </main>

      <style>{`
        .pg-link { transition: color 140ms ease, opacity 140ms ease, border-color 140ms ease; }
        .pg-link:hover { color: #e5e7eb !important; opacity: 1 !important; cursor: pointer; }
        .pg-link:focus-visible { outline: 2px solid rgba(163,230,53,0.6); outline-offset: 3px; border-radius: 12px; }
      `}</style>
    </div>
  );
};

export default Faq;

/* ----------------- styles ----------------- */

const shell: React.CSSProperties = {
  height: "100vh",
  width: "100%",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  background: "radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)",
  color: "#f9fafb",
  fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
};

const header: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 30,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 25px",
  background: "rgba(2, 6, 23, 0.9)",
  backdropFilter: "blur(10px)",
  borderBottom: "1px solid rgba(148,163,184,0.2)",
};

const headerLeft: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
};

const ghostBtn: React.CSSProperties = {
  padding: "8px 14px",
  borderRadius: 999,
  border: "1px solid #4b5563",
  background: "transparent",
  color: "#e5e7eb",
  cursor: "pointer",
  fontSize: 13,
};

const main: React.CSSProperties = {
  flex: 1,
  display: "grid",
  gridTemplateRows: "auto minmax(0, 1fr)",
  gap: 14,
  padding: "18px 7vw 18px",
  overflow: "hidden",
  boxSizing: "border-box",
};

const jumpBar: React.CSSProperties = {
  borderRadius: 22,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "linear-gradient(145deg, rgba(15,23,42,0.85), rgba(3,7,18,1))",
  boxShadow: "0 18px 45px rgba(0,0,0,0.75)",
  padding: "12px 14px",
  display: "grid",
  gridTemplateColumns: "minmax(220px, 1fr) minmax(240px, 360px)",
  gap: 12,
  alignItems: "center",
  overflow: "hidden",
};

const jumpLeft: React.CSSProperties = {
  minWidth: 220,
};

const jumpTitle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 900,
  letterSpacing: -0.02,
  color: "#e5e7eb",
};

const jumpHint: React.CSSProperties = {
  marginTop: 4,
  fontSize: 12.5,
  color: "#94a3b8",
};

const jumpRight: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
};

const searchInput: React.CSSProperties = {
  width: "100%",
  maxWidth: 360,
  borderRadius: 999,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "rgba(255,255,255,0.04)",
  padding: "10px 12px",
  color: "#e5e7eb",
  fontSize: 13.5,
  outline: "none",
};

const jumpPillsWrap: React.CSSProperties = {
  gridColumn: "1 / -1",
  display: "flex",
  gap: 10,
  overflowX: "auto",
  paddingBottom: 2,
};

const pillBtn: React.CSSProperties = {
  flex: "0 0 auto",
  borderRadius: 999,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "rgba(255,255,255,0.04)",
  padding: "8px 12px",
  color: "#cbd5e1",
  fontSize: 12.5,
  fontWeight: 800,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const pillActive: React.CSSProperties = {
  border: "1px solid rgba(163,230,53,0.35)",
  background: "rgba(163,230,53,0.10)",
  color: "#e5e7eb",
};

const contentCard: React.CSSProperties = {
  borderRadius: 22,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "linear-gradient(145deg, rgba(15,23,42,0.90), rgba(3,7,18,1))",
  boxShadow: "0 22px 55px rgba(0,0,0,0.8)",
  overflow: "hidden",
};

const contentScroll: React.CSSProperties = {
  height: "100%",
  overflowY: "auto",
  padding: "18px 18px 22px",
  boxSizing: "border-box",
};

const pageIntro: React.CSSProperties = {
  padding: "6px 2px 14px",
  borderBottom: "1px solid rgba(148,163,184,0.14)",
};

const h1: React.CSSProperties = {
  margin: 0,
  fontSize: 22,
  letterSpacing: -0.03,
  lineHeight: 1.1,
};

const subhead: React.CSSProperties = {
  margin: "10px 0 0",
  color: "#9ca3af",
  fontSize: 15,
  lineHeight: 1.55,
  maxWidth: 980,
};

const groupBlock: React.CSSProperties = {
  marginTop: 14,
  paddingTop: 2,
};

const groupHead: React.CSSProperties = {
  padding: "10px 2px 10px",
  borderTop: "1px solid rgba(148,163,184,0.14)",
};

const groupTitle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 950,
  letterSpacing: -0.02,
  color: "#e5e7eb",
};

const groupHint: React.CSSProperties = {
  marginTop: 6,
  fontSize: 13,
  color: "#94a3b8",
  lineHeight: 1.45,
};

const qaGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 12,
};

const qaCard: React.CSSProperties = {
  borderRadius: 18,
  border: "1px solid rgba(148,163,184,0.18)",
  background: "rgba(255,255,255,0.04)",
  boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
  overflow: "hidden",
};

const qBtn = (open: boolean): React.CSSProperties => ({
  width: "100%",
  textAlign: "left",
  background: open ? "rgba(163,230,53,0.08)" : "transparent",
  border: "none",
  padding: "12px 12px",
  color: "#cbd5e1",
  cursor: "pointer",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
});

const qText: React.CSSProperties = {
  fontSize: 14.5,
  fontWeight: 900,
  letterSpacing: -0.015,
  lineHeight: 1.35,
};

const chev: React.CSSProperties = {
  flex: "0 0 auto",
  width: 24,
  height: 24,
  display: "grid",
  placeItems: "center",
  borderRadius: 10,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "rgba(2, 6, 23, 0.35)",
  color: "#b1b8c7",
  fontSize: 16,
  fontWeight: 900,
  marginTop: 1,
};

const aWrap: React.CSSProperties = {
  borderTop: "1px solid rgba(148,163,184,0.14)",
  padding: "10px 12px 12px",
};

const aText: React.CSSProperties = {
  color: "#cbd5e1",
  fontSize: 13.8,
  lineHeight: 1.55,
};

const qaFooter: React.CSSProperties = {
  marginTop: 10,
  display: "flex",
  justifyContent: "flex-end",
  gap: 10,
};

const miniBtn: React.CSSProperties = {
  borderRadius: 999,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "rgba(2, 6, 23, 0.35)",
  padding: "8px 12px",
  color: "#cbd5e1",
  fontSize: 12.5,
  fontWeight: 800,
  cursor: "pointer",
};
